import type { JSX } from 'react';
import type { Attachment, CaptureBusyState, FeedbackInline, FeedbackPalette, WidgetStep } from '../types.js';
import type { WizardActions, WizardSnapshot } from '../hooks/useFeedbackWizard.js';
import { CommentIcon, SmileIcon, StackIcon, UserIcon } from '../../../_components/Icons.js';
import { CommentStep } from './CommentStep.js';
import { IdentityStep } from './IdentityStep.js';
import { RatingStep } from './RatingStep.js';
import { TypePickerStep } from './TypePickerStep.js';

interface StepBodyProps {
  palette: FeedbackPalette;
  step: WidgetStep | undefined;
  snapshot: WizardSnapshot;
  actions: WizardActions;
  inline: FeedbackInline | null;
  onRatingChange: (value: number) => void;
  onTypePicked: (value: string) => void;
  onRequestScreenshot: () => Promise<void>;
  onRequestRecording: () => Promise<void>;
}

export function StepBody({
  palette,
  step,
  snapshot,
  actions,
  inline,
  onRatingChange,
  onTypePicked,
  onRequestScreenshot,
  onRequestRecording,
}: StepBodyProps): JSX.Element | null {
  if (!step) return null;
  switch (step.kind) {
    case 'type-picker':
      return (
        <TypePickerStep
          palette={palette}
          step={step}
          selectedType={snapshot.selectedType}
          onSelect={onTypePicked}
        />
      );
    case 'rating':
      return (
        <RatingStep
          palette={palette}
          step={step}
          inline={inline}
          value={snapshot.rating}
          onChange={onRatingChange}
        />
      );
    case 'comment':
      return (
        <CommentStep
          palette={palette}
          step={step}
          comment={snapshot.comment}
          onCommentChange={actions.setComment}
          attachments={snapshot.attachments}
          onAddAttachment={(attachment: Attachment) => actions.addAttachment(attachment)}
          onRemoveAttachment={actions.removeAttachment}
          onRequestScreenshot={onRequestScreenshot}
          onRequestRecording={onRequestRecording}
          busy={snapshot.busy satisfies CaptureBusyState}
          inline={inline}
        />
      );
    case 'identity':
      return (
        <IdentityStep
          palette={palette}
          step={step}
          fullName={snapshot.fullName}
          email={snapshot.email}
          onFullNameChange={actions.setFullName}
          onEmailChange={actions.setEmail}
        />
      );
    default:
      return null;
  }
}

export function iconForStepKind(kind: WidgetStep['kind'] | undefined): JSX.Element {
  switch (kind) {
    case 'rating':
      return <SmileIcon />;
    case 'comment':
      return <CommentIcon />;
    case 'identity':
      return <UserIcon />;
    default:
      return <StackIcon />;
  }
}
